import * as ko from "knockout";
import { isBrowser } from "targetenv";

import * as pli from "../core"; // never import pli via "./index", it creates a cyclical dependency
import { PliNavigationForm, PliNavigationParams } from "./forms";


/**
 * Minimal set of methods that popup form viewmodel must implement.
 */
export interface PopupFormViewModel {
    /** Updates viewmodel to state expected on popup (called when the form is opened). */
    activate(settings: PliNavigationParams): any;

    /** Cleans up viewmodel (optional - called when the form is closed, can for example cancel operations in progress). */
    reset?(): any;
}


/**
 * Defines shape of popup form's viewmodel class (constrained by presence of a constructor with parameters).
 */
export interface PopupFormViewModelClass {
    /** Creates a new instance of a class implementing "PopupFormViewModel" interface. */
    new(formId: string, closeForm: () => void): PopupFormViewModel;
}


/**
 * Defines shape of popup form class (constrained by presence of a constructor with parameters).
 */
export interface PopupFormClass {
    /** Creates a new instance of "PopupForm" or its subclass. */
    new(originalForm: PliNavigationForm, viewModelClass: PopupFormViewModelClass): PopupForm;
}


/**
 * Replaces default form handler with PopupForm-based handler in "pli.forms.registry".
 *
 * Can be called multiple times, simply returns already
 * registered form if called before.
 */
export function registerPopupForm(formId: string, viewModelClass: PopupFormViewModelClass, popupFormClass?: PopupFormClass): PopupForm {
    let registeredForm = pli.forms.registry[formId];
    if (!registeredForm) {
        throw new Error(`Form "${formId}" has not been registered in "Pli.forms.registry" (did you call "Create*Form" in Razor?).`);
    }
    if (registeredForm.type === "PLI navigation form") {
        registeredForm = (popupFormClass
            ? new popupFormClass(registeredForm, viewModelClass)
            : new PopupForm(registeredForm, viewModelClass));
        pli.forms.registry[formId] = registeredForm;
    }

    return <PopupForm>registeredForm;
}


/**
 * Popup form handler.
 *
 * To customize open/close behaviour (e.g. to define a custom animation)
 * define a subclass of "PopupForm" and then pass the subsclass
 * as the 3rd parameter of "registerPopupForm".
 *
 * It is recommended that you export an "open{formId}" method from
 * your viewmodel TypeScript file.
 *
 * @example
 * // changePassword.ts
 *
 * export class ChangePasswordViewModel { ... } // exported for testing purposes
 *
 * export function openChangePasswordForm(settings: ChangePasswordFormSettings): PopupForm {
 *     const changePasswordForm = registerPopupForm("changePassword", ChangePasswordViewModel);
 *     return changePasswordForm.open(settings);
 * }
 */
export class PopupForm implements PliNavigationForm {

    // -------------------------------------------------------------------------
    // Construction & destruction
    // -------------------------------------------------------------------------

    /**
     * Creates a new instance of "PopupForm", validating and storing specified "originalForm" and "viewModelConstructor" references.
     *
     * Original form is expected to be the default form created
     * during HTML page load by looking for elements that have
     * "data-pli-form" attribute. Original form is validated
     * to force users of the form to render the form into
     * the web page (typically by using a Razor extension
     * method "Create{formId}Form").
     *
     * To test "PopupForm" and subclasses, use helper function
     * "addToPliFormRegistry" from "util/testHelpers" to create
     * a fake original form, allowing "PopupForm" constructor
     * to get past the validation checks.
     */
    constructor(originalForm: PliNavigationForm, viewModelConstructor: PopupFormViewModelClass) {
        if (!originalForm) { throw new Error("Original form must be specified."); }
        if (!originalForm.id) { throw new Error("Specified original form must have a valid \"id\" property."); }
        if (!originalForm.$form) { throw new Error(`Specified original form for "${originalForm.id}" must have a valid \"$form\" property.`); }
        if (originalForm.isRoot) { throw new Error(`Specified original form for "${originalForm.id}" must not be be a root form.`); }
        if (typeof viewModelConstructor !== "function" || !viewModelConstructor) { throw new Error("ViewModel constructor myst be specified in PopupForm.constructor."); }

        this.id = originalForm.id;
        this.type = `${this.id} (PLI navigation form)`;
        this.$form = originalForm.$form;
        this._originalForm = originalForm;
        this._viewModelConstructor = viewModelConstructor;
    }

    /**
     * Prevents "util/disposable.disposeObject" from entering an infinite loop.
     *
     * There is an infinite loop where every form points to
     * its navigation page which points back to the form.
     */
    dispose(): void {
        return;
    }



    // -------------------------------------------------------------------------
    // Public interface
    // -------------------------------------------------------------------------

    /**
     * Gets root element of this form wrapped in a jQuery array.
     */
    $form: JQuery; // initialized in "constructor", readonly, never null

    /**
     * Closes this form.
     *
     * Subclasses should customize closing behaviour by overriding
     * "_close" method, not this public "close".
     */
    close(): this {
        if (pli.keys.isKeymapRegistered(this.id)) {
            pli.keys.deactivateKeymap(this.id);
        }

        this._close(this._viewModel, this._originalForm);

        return this;
    }

    /**
     * Gets unique identifier of this form (readonly, never null or an empty string).
     */
    id: string;

    /**
     * Gets a flag indicating whether this form is HTML page's root form (readonly, always false).
     */
    isRoot = false;

    /**
     * Opens this form.
     *
     * Subclasses should customize opening behaviour by overriding
     * "_open" method, not this public "open".
     */
    open(settings?: PliNavigationParams): this {
        if (!this._viewModel) {
            this._viewModel = new this._viewModelConstructor(this.id, this.close.bind(this));
            if (isBrowser) {
                ko.applyBindings(this._viewModel, this.$form[0]);
            }
        }

        this._open(settings, this._viewModel, this._originalForm);

        if (pli.keys.isKeymapRegistered(this.id)) {
            pli.keys.activateKeymap(this.id);
        }

        return this;
    }

    /**
     * Gets type of this navigation form (for debugging, readonly, set to `${formId} (PLI navigation form)`).
     */
    type: string; // initialized in "constructor", readonly, never null



    // -------------------------------------------------------------------------
    // Protected interface
    // -------------------------------------------------------------------------

    /** Overridable form-close logic. */
    protected _close(viewModel: PopupFormViewModel, originalForm: PliNavigationForm): void {
        originalForm.close();
        viewModel.reset();
    }

    /** Overridable form-open logic. */
    protected _open(settings: PliNavigationParams, viewModel: PopupFormViewModel, originalForm: PliNavigationForm): void {
        originalForm.open(settings);
        viewModel.activate(settings);
    }


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /** Reference to the original form we replaced. */
    private _originalForm: PliNavigationForm; // initialized in "constructor", readonly, never null

    /** Viewmodel used by this form. */
    private _viewModel: PopupFormViewModel = null; // initialized in "open", lazy (readonly after initialization)

    /** Constructor that will be used to create viewmodel on first open */
    private _viewModelConstructor: PopupFormViewModelClass; // initialized in "constructor", readonly, never null

}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms/popupForm.ts